import { trigger, state, style, transition, animate } from '@angular/animations';
import { ChangeDetectorRef, Component, Input, OnDestroy, AfterViewInit } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { Subject, auditTime, filter, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-tree-drill-down',
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    DataErrorComponent,
    MatProgressSpinnerModule,
    CapitalCasePipe,
  ],
  templateUrl: './tree-drill-down.component.html',
  styleUrl: './tree-drill-down.component.scss',
  animations: [
    trigger('slide', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('220ms ease', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease', style({ height: 0, opacity: 0 }))]),
    ]),
  ],
})
export class TreeDrillDownComponent implements AfterViewInit, OnDestroy {
  @Input() data: any;

  treeData: any[] = [];
  title: string = '';
  productId: string = '';
  loading = false;
  hasError = false;
  errorMessage = '';

  // Tracks which nodes are expanded by object reference
  expandedNodes = new Set<any>();

  private destroy$ = new Subject<void>();

  constructor(
    private service: ConfigService,
    private cdr: ChangeDetectorRef,
    private filterState: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
    private responsiveLayoutService: ResponsiveLayoutService,
  ) {}

  get resolvedHeight(): string {
    return this.data?.height || this.responsiveLayoutService.getDefaultWidgetHeight();
  }

  get isScoreEditable(): boolean {
    return this.toBoolean(this.data?.editableBox ?? this.data?.editablebox);
  }

  ngAfterViewInit() {
    // Initial load
    this.loadData({});

    // Reload when global filters change, respecting filterListner targeting
    this.filterState.filter$
      .pipe(
        auditTime(500),
        filter((f) => !!f),
        takeUntil(this.destroy$),
      )
      .subscribe((filters) => {
        // Only reload if this widget is a target of the filter event
        if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) return;
        const activeFilters = filters?.requestParams || filters?.filter || filters;
        this.loadData(activeFilters);
      });
  }

  /** Single entry point for node click */
  handleNodeClick(node: any): void {
    if (node.children?.length) {
      // Branch node: just expand/collapse
      this.toggleNode(node);
    } else {
      // Leaf node (no further drill-down): emit filter selection
      this.chartFilterHandler.updateFilterSelection(this.data, node.name);
    }
  }

  protected loadData(activeFilters: any = {}): void {
    if (!this.data?.apiUrl) return;
    this.loading = true;
    this.hasError = false;
    // Debug: log API URL and activeFilters to help trace requests in browser console
    // (temporary; remove after debugging)
    // eslint-disable-next-line no-console
    console.log('[TreeDrillDown] loading from', this.data?.apiUrl, 'filters:', activeFilters);

    this.service
      .getConfig(this.data.apiUrl, this.data, activeFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.extractTree(res);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.hasError = true;
          this.errorMessage = 'Failed to load data.';
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  protected extractTree(res: any): void {
    // Support new platform format (dataKey), legacy format (value key), or direct array
    const key = this.data?.dataKey || this.data?.value || 'responseData';
    const list: any[] = res?.[key] ?? (Array.isArray(res) ? res : [res]);

    if (!list?.length) {
      this.hasError = true;
      this.errorMessage = 'No data found.';
      return;
    }

    const first = list[0];
    this.productId = first?.productId ?? '';
    this.title = this.data?.title
      ? (this.productId ? `${this.productId} – ` : '') + this.data.title
      : this.productId;

    this.treeData = Array.isArray(first?.drillDownTree) ? first.drillDownTree : [];
  }

  getScoreColor(score: any): { [key: string]: string } {
    const n = Number(score);
    if (score == null || isNaN(n)) return { 'background-color': '#adacac60' };
    if (n >= 0 && n < 4) return { 'background-color': '#c4312b' };
    if (n >= 4 && n < 8) return { 'background-color': '#FFFF66' };
    if (n >= 8 && n <= 10) return { 'background-color': '#4CAF50' };
    return { 'background-color': '#adacac60' };
  }

  formatScore(value: any): string {
    if (value == null) return '–';
    const n = Number(value);
    return isNaN(n) ? String(value) : n % 1 === 0 ? String(n) : n.toFixed(2);
  }

  onScoreInput(node: any, value: string): void {
    if (!node) {
      return;
    }

    node.score = value === '' ? null : Number(value);
  }

  private toBoolean(value: any): boolean {
    return value === true || String(value).toLowerCase() === 'true';
  }

  toggleNode(node: any): void {
    if (this.expandedNodes.has(node)) {
      this.expandedNodes.delete(node);
    } else {
      this.expandedNodes.add(node);
    }
    this.cdr.detectChanges();
  }

  isExpanded(node: any): boolean {
    return this.expandedNodes.has(node);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
